import {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { ApiPaginatedEntityResponse, dispatchCreateNotification } from 'src/action-creators'
import { useUserSellerCompanyId } from 'src/contexts/userContext'
import { useSegment } from 'src/hooks'
import {
  useEntityQueries,
  useEntityQuery,
  usePatchEntityMutation,
  usePostEntityMutation,
} from 'src/queries/entity'
import { GenerateQueriesArgs, generateQueries } from 'src/queries/queryGenerator'
import { isListQuery } from 'src/queries/utils'
import { BuyerCompany } from 'src/types'
import { roundHalfToEven } from 'src/utils'

export const BUYER_COMPANIES_ENDPOINT = 'seller/buyer_companies'
export const BUYER_COMPANIES_QUERY_KEY = 'buyerCompanies'

export interface GetBuyerCompaniesRequest {
  id?: Id | Id[]
  canUseChat?: boolean
  deliverable?: boolean
  deliverableDirect?: boolean
  editable?: boolean
  hasContract?: boolean
  hasConnectedRoute?: boolean
  limit?: number
  ordering?: string
  q?: string
  sellerCompanyId?: number
}

const generateQueriesConfig: GenerateQueriesArgs<BuyerCompany, GetBuyerCompaniesRequest> = {
  endpoint: BUYER_COMPANIES_ENDPOINT,
  queryKey: BUYER_COMPANIES_QUERY_KEY,
}

const { usePaginatedEntitiesQuery, useRecursiveQuery } = generateQueries(generateQueriesConfig)

export const useBuyerCompanyQuery = (
  id: Id | undefined,
  queryOptions?: UseQueryOptions<BuyerCompany, Error>,
  queryParams?: Partial<GetBuyerCompaniesRequest>
) => {
  const sellerCompanyId = useUserSellerCompanyId()

  return useEntityQuery({
    endpoint: BUYER_COMPANIES_ENDPOINT,
    entityId: id,
    queryKey: BUYER_COMPANIES_QUERY_KEY,
    queryParams: { ...queryParams, sellerCompanyId },
    queryOptions: {
      ...queryOptions,
      enabled: !!sellerCompanyId && queryOptions?.enabled,
    },
  })
}

export const useBuyerCompanyQueries = (ids: Id[]) => {
  const sellerCompanyId = useUserSellerCompanyId()

  return useEntityQueries<BuyerCompany, GetBuyerCompaniesRequest>({
    endpoint: BUYER_COMPANIES_ENDPOINT,
    entityIds: ids,
    queryKey: BUYER_COMPANIES_QUERY_KEY,
    queryParams: { sellerCompanyId },
    queryOptions: {
      enabled: !!sellerCompanyId,
    },
  }) as UseQueryResult<BuyerCompany, Error>[]
}

export interface UsePaginatedBuyerCompaniesQueryArgs<TData> {
  queryParams?: Partial<GetBuyerCompaniesRequest>
  queryOptions?: UseInfiniteQueryOptions<
    ApiPaginatedEntityResponse<BuyerCompany>,
    Error,
    ApiPaginatedEntityResponse<TData>
  >
}

export const usePaginatedBuyerCompaniesQuery = <TData = BuyerCompany>({
  queryParams,
  queryOptions,
}: UsePaginatedBuyerCompaniesQueryArgs<TData> = {}) => {
  const sellerCompanyId = useUserSellerCompanyId()

  return usePaginatedEntitiesQuery<TData>({
    queryParams: { ...queryParams, sellerCompanyId },
    queryOptions: {
      ...queryOptions,
      enabled: !!sellerCompanyId && queryOptions?.enabled,
    },
  })
}

export const useRecursiveBuyerCompaniesQuery = <TData = BuyerCompany>({
  queryParams,
  queryOptions,
}: UsePaginatedBuyerCompaniesQueryArgs<TData> = {}) => {
  const sellerCompanyId = useUserSellerCompanyId()

  return useRecursiveQuery<TData>({
    queryParams: { ...queryParams, sellerCompanyId },
    queryOptions: {
      ...queryOptions,
      enabled: !!sellerCompanyId && queryOptions?.enabled,
    },
  })
}

interface AugmentedBuyerCompany extends BuyerCompany {
  transferIban?: string | null
  transferBic?: string | null
  transferBank?: string | null
  qrBillIban?: string | null
  qrBillMembershipNumber?: string | null
  qrBillBank?: string | null
  sellerCompanyId: Id
}

interface BuyerCompanyMutationDeps {
  nextPage?: string
}

export const usePatchBuyerCompany = (id: Id) => {
  const [startTime] = useState(Date.now)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { track } = useSegment()
  const sellerCompanyId = useUserSellerCompanyId()

  return usePatchEntityMutation<AugmentedBuyerCompany>({
    endpoint: BUYER_COMPANIES_ENDPOINT,
    entityId: id,
    queryOptions: {
      onSuccess: newBuyerCompany => {
        dispatchCreateNotification(dispatch)({
          message: gettext('The customer has been successfully updated'),
          type: 'positive',
        })
        queryClient.setQueryData(
          [BUYER_COMPANIES_QUERY_KEY, id, { sellerCompanyId }],
          newBuyerCompany
        )
        queryClient.removeQueries({
          predicate: isListQuery(BUYER_COMPANIES_QUERY_KEY),
        })
        track('Seller - Customer Updated', {
          conversionTime: roundHalfToEven((Date.now() - startTime) / 1000),
          customerId: id,
        })
      },
    },
  })
}

export const usePostBuyerCompany = ({ nextPage }: BuyerCompanyMutationDeps = {}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { track } = useSegment()
  const [startTime] = useState(Date.now)
  const { push } = useHistory()

  return usePostEntityMutation<AugmentedBuyerCompany>({
    endpoint: BUYER_COMPANIES_ENDPOINT,
    queryOptions: {
      onSuccess: newCustomer => {
        dispatchCreateNotification(dispatch)({
          message: gettext('The customer has been successfully created'),
          type: 'positive',
        })
        queryClient.removeQueries({
          predicate: isListQuery(BUYER_COMPANIES_QUERY_KEY),
        })
        track('Seller - Customer Created', {
          conversionTime: roundHalfToEven((Date.now() - startTime) / 1000),
          customerId: newCustomer.id,
        })
        if (nextPage) {
          push(`${nextPage}${newCustomer.id}`, { shouldScrollToBottom: true })
        }
      },
    },
  })
}
